import React, { useState } from "react";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField } from "@material-ui/core";
import { CompanyReservation } from "../../../../model/CompanyReservation";
import { Alert } from "@material-ui/lab";
import { ApiBackend } from "../../../../providers/apibackend";

type EditReservationDialogProps = {
    companyId: string;
    reservation: CompanyReservation;
    onSave: () => void;
    onAbort: () => void;
};

const EditReservationDialog = (props: EditReservationDialogProps) => {
    const [numItems, setNumItems] = useState(props.reservation.total);
    const [reservationName, setReservationName] = useState(props.reservation.reservationName);
    const [hasError, setHasError] = useState(false);
    const [isUpdatingOrder, setIsUpdatinggOrder] = useState(false);
    const [isUpdateComplete, setIsUpdateComplete] = useState(false);

    const api = new ApiBackend();

    const handleUpdate = async () => {
        const { companyId, reservation } = props;

        setIsUpdatinggOrder(true);

        try {
            const res = await api.updateCompanyReservation(companyId, reservation.orderId, numItems, reservationName);
            if (!res) {
                throw new Error();
            }

            setIsUpdateComplete(true);
        } catch (err) {
            setHasError(true);
        } finally {
            setIsUpdatinggOrder(false);
        }
    };

    const hasChanges = () => {
        const { reservation } = props;

        return numItems !== reservation.total || reservationName !== reservation.reservationName;
    };

    const render = () => {
        const { reservation, onAbort, onSave } = props;

        const minItems = reservation.numUsed;
        const isSaveDisabled = !hasChanges() || isUpdatingOrder;
        const gridStyle: React.CSSProperties = isUpdateComplete || isUpdatingOrder ? { opacity: 0.5, pointerEvents: "none", userSelect: "none" } : null;

        return (
            <Dialog
                open={true}
                disableEscapeKeyDown
                fullWidth={true}
                maxWidth="lg"
            >
                <DialogTitle>
                    Redigera reservation för {reservation.productName}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} style={gridStyle}>
                        <Grid item xs={12}>
                            <Alert severity="info">Ursprungligt namn: <b>{reservation.reservationName}</b>, ursprungligt antal: <b>{reservation.total}</b> varav nyttjade: <b>{reservation.numUsed}</b></Alert>
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                name="reservationName"
                                InputLabelProps={{ shrink: true }}
                                value={reservationName}
                                label="Namn på reservation (fritext)"
                                fullWidth
                                onChange={(e: any) => {
                                    setReservationName(e.target.value);
                                }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                name="numItems"
                                InputLabelProps={{ shrink: true }}
                                value={numItems}
                                label={`Totalt antal (minimum ${minItems})`}
                                fullWidth
                                type="number"
                                InputProps={{ inputProps: { min: minItems } }}
                                onChange={(e: any) => {
                                    setNumItems(+e.target.value);
                                }}
                            />
                        </Grid>
                    </Grid>
                    {hasError &&
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Alert severity="error">Någonting gick fel när reservation skulle uppdateras</Alert>
                            </Grid>
                        </Grid>
                    }
                    {isUpdateComplete &&
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Alert severity="info">Reservation uppdaterad. Du kan nu stänga dialogen.</Alert>
                            </Grid>
                        </Grid>
                    }
                </DialogContent>
                <DialogActions>
                    <Box style={{ display: "flex", lineHeight: "37px", width: "100%" }}>
                        <Box style={{ flex: 1 }}>
                            {isUpdatingOrder && <>
                                <CircularProgress color="secondary" />
                            </>}
                        </Box>
                        <Box style={{ display: "flex", flex: 1, gap: 8, justifyContent: "flex-end" }}>
                            {isUpdateComplete ?
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    disabled={isUpdatingOrder}
                                    onClick={() => {
                                        onSave();
                                    }}
                                >
                                    Stäng
                                </Button>
                                :
                                <>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={isSaveDisabled}
                                        onClick={handleUpdate}
                                    >
                                        Uppdatera reservation
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        disabled={isUpdatingOrder}
                                        onClick={() => {
                                            onAbort();
                                        }}
                                    >
                                        Avbryt
                                    </Button>
                                </>
                            }
                        </Box>
                    </Box>
                </DialogActions>
            </Dialog>
        );
    };

    return render();
}

export default EditReservationDialog;
